import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { adminApi } from '../api/admin';
import { Navigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Shield, Users, Building2, Plus, Pencil, Trash2,
  CheckCircle, XCircle, ChevronRight, UserPlus, Eye, DatabaseZap,
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import EmptyState from '../components/EmptyState';

// ── Small helpers ────────────────────────────────────────────

function Badge({ label, variant }: { label: string; variant: 'green' | 'red' | 'blue' | 'gray' }) {
  const cls = {
    green: 'text-ok',
    red: 'text-err',
    blue: 'text-primary',
    gray: 'text-muted',
  }[variant];
  return <span className={`inline-block text-xs font-semibold ${cls}`}>{label}</span>;
}

const MEMBER_ROLES = ['owner', 'admin', 'technician', 'customer'];

// ── Overview Tab ─────────────────────────────────────────────

function OverviewTab({ stats }: { stats: any }) {
  if (!stats) return <p className="text-muted text-sm">Loading…</p>;
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {[
        { label: 'Tenants', value: stats.totalCompanies, icon: Building2 },
        { label: 'Users', value: stats.totalUsers, icon: Users },
        { label: 'Tickets', value: stats.totalTickets, icon: Shield },
        { label: 'Time tracked (hrs)', value: stats.totalTimeHours, icon: CheckCircle },
      ].map(({ label, value, icon: Icon }) => (
        <div key={label} className="card p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg brand-gradient flex items-center justify-center flex-shrink-0">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-sm text-muted">{label}</p>
            <p className="text-2xl font-semibold text-fg">{value}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Tenants Tab ──────────────────────────────────────────────

function TenantsTab() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editCompany, setEditCompany] = useState<any>(null);
  const [membersCompany, setMembersCompany] = useState<any>(null);
  const [createForm, setCreateForm] = useState({ name: '', ownerEmail: '' });
  const [editForm, setEditForm] = useState({ name: '' });
  const [formError, setFormError] = useState('');
  const [seedingId, setSeedingId] = useState<string | null>(null);
  const [seedResult, setSeedResult] = useState<{ companyName: string; data: any } | null>(null);

  const { data } = useQuery({
    queryKey: ['admin-companies', search],
    queryFn: () => adminApi.getCompanies({ search: search || undefined, page: 1, limit: 50 }),
  });
  const companies = data?.companies ?? [];

  const createMut = useMutation({
    mutationFn: () => adminApi.createCompany(createForm),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-companies'] }); qc.invalidateQueries({ queryKey: ['admin-stats'] }); setShowCreate(false); setCreateForm({ name: '', ownerEmail: '' }); setFormError(''); },
    onError: (e: any) => setFormError(e?.response?.data?.error || 'Failed to create tenant'),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => adminApi.updateCompany(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-companies'] }); setEditCompany(null); setFormError(''); },
    onError: (e: any) => setFormError(e?.response?.data?.error || 'Failed to update tenant'),
  });

  const toggleActive = (c: any) => updateMut.mutate({ id: c.id, data: { isActive: !c.isActive } });

  const openEdit = (c: any) => { setEditForm({ name: c.name }); setEditCompany(c); setFormError(''); };

  const seedDemo = async (c: any) => {
    if (seedingId) return;
    setSeedingId(c.id);
    try {
      const data = await adminApi.seedDemoData(c.id);
      qc.invalidateQueries({ queryKey: ['admin-companies'] });
      qc.invalidateQueries({ queryKey: ['admin-stats'] });
      setSeedResult({ companyName: c.name, data });
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to load demo data');
    } finally {
      setSeedingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <input type="search" placeholder="Search tenants…" className="input max-w-xs" value={search} onChange={e => setSearch(e.target.value)} />
        <button className="btn btn-primary flex items-center gap-1.5" onClick={() => { setShowCreate(true); setFormError(''); }}>
          <Plus className="w-4 h-4" /> New Tenant
        </button>
      </div>

      <div className="card overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="scpa-table">
            <thead>
              <tr>
                <th className="scpa-th">Name</th>
                <th className="scpa-th">Slug</th>
                <th className="scpa-th">Owner</th>
                <th className="scpa-th">Members</th>
                <th className="scpa-th">Tickets</th>
                <th className="scpa-th">Status</th>
                <th className="scpa-th" />
              </tr>
            </thead>
            <tbody>
              {companies.map((c: any) => (
                <tr key={c.id} className="scpa-row">
                  <td className="scpa-td font-medium text-fg">{c.name}</td>
                  <td className="scpa-td text-muted font-mono text-xs">{c.slug}</td>
                  <td className="scpa-td text-muted">{c.owner?.name ?? c.owner?.email ?? '—'}</td>
                  <td className="scpa-td text-muted">{c._count?.members ?? 0}</td>
                  <td className="scpa-td text-muted">{c._count?.tickets ?? 0}</td>
                  <td className="scpa-td">
                    <Badge label={c.isActive ? 'Active' : 'Inactive'} variant={c.isActive ? 'green' : 'gray'} />
                  </td>
                  <td className="scpa-td">
                    <div className="flex items-center gap-1 justify-end">
                      <button title="Load Demo Data" onClick={() => seedDemo(c)} disabled={seedingId === c.id} className="p-1.5 rounded hover:bg-row-odd text-muted hover:text-warn disabled:opacity-50">
                        <DatabaseZap className="w-4 h-4" />
                      </button>
                      <button title="View Members" onClick={() => setMembersCompany(c)} className="p-1.5 rounded hover:bg-row-odd text-muted hover:text-primary">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button title="Edit" onClick={() => openEdit(c)} className="p-1.5 rounded hover:bg-row-odd text-muted hover:text-primary">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button title={c.isActive ? 'Deactivate' : 'Activate'} onClick={() => toggleActive(c)} className={`p-1.5 rounded hover:bg-row-odd ${c.isActive ? 'text-muted hover:text-err' : 'text-muted hover:text-ok'}`}>
                        {c.isActive ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {companies.length === 0 && (
            <EmptyState icon={<Building2 className="w-6 h-6" />}>
              No tenants found. Click <strong>New Tenant</strong> to create one.
            </EmptyState>
          )}
        </div>
      </div>

      {/* Seed result modal */}
      <Modal open={!!seedResult} onClose={() => setSeedResult(null)} title="Demo Data Loaded" footer={
        <button className="btn btn-primary" onClick={() => setSeedResult(null)}>Done</button>
      }>
        {seedResult && (
          <div className="space-y-4">
            <p className="text-sm text-muted">
              Demo data has been created for <strong className="text-fg">{seedResult.companyName}</strong>.
            </p>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(seedResult.data).map(([key, val]) => (
                <div key={key} className="bg-row-odd rounded-lg px-3 py-2">
                  <p className="text-xs text-muted capitalize">{key.replace(/([A-Z])/g, ' $1')}</p>
                  <p className="text-lg font-semibold text-fg">{String(val)}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted">All demo emails contain <code className="font-mono">_demo_</code> for easy identification and removal.</p>
          </div>
        )}
      </Modal>

      {/* Create modal */}
      <Modal open={showCreate} onClose={() => { setShowCreate(false); setFormError(''); }} title="New Tenant" busy={createMut.isPending} footer={
        <>
          <button className="btn btn-secondary" onClick={() => { setShowCreate(false); setFormError(''); }}>Cancel</button>
          <button className="btn btn-primary" disabled={createMut.isPending} onClick={() => createMut.mutate()}>
            {createMut.isPending ? 'Creating…' : 'Create Tenant'}
          </button>
        </>
      }>
        <div className="space-y-4">
          <div>
            <label className="label">Tenant Name</label>
            <input className="input w-full" placeholder="Acme Corp" value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <label className="label">Owner Email</label>
            <input className="input w-full" type="email" placeholder="owner@example.com" value={createForm.ownerEmail} onChange={e => setCreateForm(f => ({ ...f, ownerEmail: e.target.value }))} />
            <p className="text-xs text-muted mt-1">Must be an existing user. They will be set as owner.</p>
          </div>
          {formError && <p className="text-sm text-err">{formError}</p>}
        </div>
      </Modal>

      {/* Edit modal */}
      <Modal open={!!editCompany} onClose={() => { setEditCompany(null); setFormError(''); }} title={editCompany ? `Edit: ${editCompany.name}` : 'Edit'} busy={updateMut.isPending} footer={
        <>
          <button className="btn btn-secondary" onClick={() => { setEditCompany(null); setFormError(''); }}>Cancel</button>
          <button className="btn btn-primary" disabled={updateMut.isPending} onClick={() => updateMut.mutate({ id: editCompany.id, data: { name: editForm.name } })}>
            {updateMut.isPending ? 'Saving…' : 'Save Changes'}
          </button>
        </>
      }>
        <div className="space-y-4">
          <div>
            <label className="label">Tenant Name</label>
            <input className="input w-full" value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          {formError && <p className="text-sm text-err">{formError}</p>}
        </div>
      </Modal>

      {/* Members modal */}
      {membersCompany && (
        <TenantMembersModal company={membersCompany} onClose={() => setMembersCompany(null)} />
      )}
    </div>
  );
}

function TenantMembersModal({ company, onClose }: { company: any; onClose: () => void }) {
  const qc = useQueryClient();
  const [addEmail, setAddEmail] = useState('');
  const [addRole, setAddRole] = useState('technician');
  const [error, setError] = useState('');

  const { data: members = [], isLoading } = useQuery({
    queryKey: ['admin-company-members', company.id],
    queryFn: () => adminApi.getCompanyMembers(company.id),
  });

  const addMut = useMutation({
    mutationFn: () => adminApi.addCompanyMember(company.id, { userEmail: addEmail, role: addRole }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-company-members', company.id] }); setAddEmail(''); setError(''); },
    onError: (e: any) => setError(e?.response?.data?.error || 'Failed to add member'),
  });

  const removeMut = useMutation({
    mutationFn: (userId: string) => adminApi.removeCompanyMember(company.id, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-company-members', company.id] }),
  });

  const roleMut = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) => adminApi.updateMemberRole(company.id, userId, role),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-company-members', company.id] }),
  });

  return (
    <Modal open onClose={onClose} title={`${company.name} — Members`}>
      <div className="space-y-4">
        {/* Add member */}
        <div className="flex gap-2">
          <input className="input flex-1" type="email" placeholder="user@example.com" value={addEmail} onChange={e => setAddEmail(e.target.value)} />
          <select className="select" value={addRole} onChange={e => setAddRole(e.target.value)}>
            {MEMBER_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <button className="btn btn-primary flex items-center gap-1" disabled={addMut.isPending || !addEmail} onClick={() => addMut.mutate()}>
            <UserPlus className="w-4 h-4" />
          </button>
        </div>
        {error && <p className="text-sm text-err">{error}</p>}

        {/* Members list */}
        {isLoading ? <p className="text-sm text-muted">Loading…</p> : members.length === 0 ? (
          <EmptyState icon={<Users className="w-6 h-6" />}>No members yet. Add one above by email.</EmptyState>
        ) : (
          <div className="divide-y divide-[var(--scpa-border-light)] max-h-72 overflow-y-auto">
            {members.map((m: any) => (
              <div key={m.id} className="flex items-center gap-3 py-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-fg truncate">{m.user.name}</p>
                  <p className="text-xs text-muted truncate">{m.user.email}</p>
                </div>
                <select
                  className="select text-xs py-1"
                  value={m.role}
                  onChange={e => roleMut.mutate({ userId: m.user.id, role: e.target.value })}
                >
                  {MEMBER_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <button onClick={() => removeMut.mutate(m.user.id)} className="p-1 rounded text-muted hover:text-err hover:bg-row-odd">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Users Tab ────────────────────────────────────────────────

function UsersTab() {
  const qc = useQueryClient();
  const { user: currentUser } = useAuth();
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editUser, setEditUser] = useState<any>(null);
  const [viewUser, setViewUser] = useState<any>(null);
  const [confirmDelete, setConfirmDelete] = useState<any>(null);
  const [createForm, setCreateForm] = useState({ name: '', email: '', password: '', isSuperAdmin: false });
  const [editForm, setEditForm] = useState({ name: '', isSuperAdmin: false });
  const [formError, setFormError] = useState('');

  const { data } = useQuery({
    queryKey: ['admin-users', search],
    queryFn: () => adminApi.getUsers({ search: search || undefined, page: 1, limit: 50 }),
  });
  const users = data?.users ?? [];

  const createMut = useMutation({
    mutationFn: () => adminApi.createUser(createForm),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-users'] }); qc.invalidateQueries({ queryKey: ['admin-stats'] }); setShowCreate(false); setCreateForm({ name: '', email: '', password: '', isSuperAdmin: false }); setFormError(''); },
    onError: (e: any) => setFormError(e?.response?.data?.error || 'Failed to create user'),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => adminApi.updateUser(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-users'] }); setEditUser(null); setFormError(''); },
    onError: (e: any) => setFormError(e?.response?.data?.error || 'Failed to update user'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => adminApi.deleteUser(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-users'] }); qc.invalidateQueries({ queryKey: ['admin-stats'] }); setConfirmDelete(null); },
  });

  const openEdit = (u: any) => { setEditForm({ name: u.name, isSuperAdmin: u.isSuperAdmin }); setEditUser(u); setFormError(''); };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <input type="search" placeholder="Search users…" className="input max-w-xs" value={search} onChange={e => setSearch(e.target.value)} />
        <button className="btn btn-primary flex items-center gap-1.5" onClick={() => { setShowCreate(true); setFormError(''); }}>
          <Plus className="w-4 h-4" /> New User
        </button>
      </div>

      <div className="card overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="scpa-table">
            <thead>
              <tr>
                <th className="scpa-th">Name</th>
                <th className="scpa-th">Email</th>
                <th className="scpa-th">Role</th>
                <th className="scpa-th">Tenants</th>
                <th className="scpa-th" />
              </tr>
            </thead>
            <tbody>
              {users.map((u: any) => (
                <tr key={u.id} className="scpa-row">
                  <td className="scpa-td font-medium text-fg">
                    {u.name}
                    {u.id === currentUser?.id && <span className="ml-2 text-xs text-muted">(you)</span>}
                  </td>
                  <td className="scpa-td text-muted">{u.email}</td>
                  <td className="scpa-td">
                    {u.isSuperAdmin
                      ? <Badge label="Super Admin" variant="blue" />
                      : <Badge label="User" variant="gray" />}
                  </td>
                  <td className="scpa-td text-muted">{u._count?.companyMembers ?? 0}</td>
                  <td className="scpa-td">
                    <div className="flex items-center gap-1 justify-end">
                      <button title="View Tenants" onClick={() => setViewUser(u)} className="p-1.5 rounded hover:bg-row-odd text-muted hover:text-primary">
                        <ChevronRight className="w-4 h-4" />
                      </button>
                      <button title="Edit" onClick={() => openEdit(u)} className="p-1.5 rounded hover:bg-row-odd text-muted hover:text-primary">
                        <Pencil className="w-4 h-4" />
                      </button>
                      {u.id !== currentUser?.id && (
                        <button title="Deactivate" onClick={() => setConfirmDelete(u)} className="p-1.5 rounded hover:bg-row-odd text-muted hover:text-err">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.length === 0 && (
            <EmptyState icon={<Users className="w-6 h-6" />}>
              No users found. Click <strong>New User</strong> to create one.
            </EmptyState>
          )}
        </div>
      </div>

      {/* Create modal */}
      <Modal open={showCreate} onClose={() => { setShowCreate(false); setFormError(''); }} title="New User" busy={createMut.isPending} footer={
        <>
          <button className="btn btn-secondary" onClick={() => { setShowCreate(false); setFormError(''); }}>Cancel</button>
          <button className="btn btn-primary" disabled={createMut.isPending} onClick={() => createMut.mutate()}>
            {createMut.isPending ? 'Creating…' : 'Create User'}
          </button>
        </>
      }>
        <div className="space-y-4">
          <div>
            <label className="label">Full Name</label>
            <input className="input w-full" placeholder="Jane Smith" value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <label className="label">Email</label>
            <input className="input w-full" type="email" placeholder="jane@example.com" value={createForm.email} onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} />
          </div>
          <div>
            <label className="label">Password</label>
            <input className="input w-full" type="password" placeholder="Min 8 characters" value={createForm.password} onChange={e => setCreateForm(f => ({ ...f, password: e.target.value }))} />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="createSuperAdmin" checked={createForm.isSuperAdmin} onChange={e => setCreateForm(f => ({ ...f, isSuperAdmin: e.target.checked }))} className="w-4 h-4 accent-primary" />
            <label htmlFor="createSuperAdmin" className="text-sm font-medium text-fg">Global Super Admin</label>
          </div>
          {formError && <p className="text-sm text-err">{formError}</p>}
        </div>
      </Modal>

      {/* Edit modal */}
      <Modal open={!!editUser} onClose={() => { setEditUser(null); setFormError(''); }} title={editUser ? `Edit: ${editUser.name}` : 'Edit'} busy={updateMut.isPending} footer={
        <>
          <button className="btn btn-secondary" onClick={() => { setEditUser(null); setFormError(''); }}>Cancel</button>
          <button className="btn btn-primary" disabled={updateMut.isPending} onClick={() => updateMut.mutate({ id: editUser.id, data: editForm })}>
            {updateMut.isPending ? 'Saving…' : 'Save Changes'}
          </button>
        </>
      }>
        <div className="space-y-4">
          <div>
            <label className="label">Full Name</label>
            <input className="input w-full" value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="editSuperAdmin" checked={editForm.isSuperAdmin} onChange={e => setEditForm(f => ({ ...f, isSuperAdmin: e.target.checked }))} className="w-4 h-4 accent-primary" />
            <label htmlFor="editSuperAdmin" className="text-sm font-medium text-fg">Global Super Admin</label>
          </div>
          <p className="text-xs text-muted">Super admins have full access to all tenants and this admin panel.</p>
          {formError && <p className="text-sm text-err">{formError}</p>}
        </div>
      </Modal>

      {/* Confirm delete */}
      <Modal open={!!confirmDelete} onClose={() => setConfirmDelete(null)} title="Deactivate User" width={440} busy={deleteMut.isPending} footer={
        <>
          <button className="btn btn-secondary" onClick={() => setConfirmDelete(null)}>Cancel</button>
          <button className="btn btn-danger" disabled={deleteMut.isPending} onClick={() => deleteMut.mutate(confirmDelete.id)}>
            {deleteMut.isPending ? 'Deactivating…' : 'Deactivate'}
          </button>
        </>
      }>
        {confirmDelete && (
          <p className="text-sm text-fg">
            Deactivate <strong>{confirmDelete.name}</strong> ({confirmDelete.email})? They will not be able to log in.
          </p>
        )}
      </Modal>

      {/* View user tenants */}
      {viewUser && <UserTenantsModal user={viewUser} onClose={() => setViewUser(null)} />}
    </div>
  );
}

function UserTenantsModal({ user, onClose }: { user: any; onClose: () => void }) {
  const { data: memberships = [], isLoading } = useQuery({
    queryKey: ['admin-user-companies', user.id],
    queryFn: () => adminApi.getUserCompanies(user.id),
  });

  return (
    <Modal open onClose={onClose} title={`${user.name} — Tenants`}>
      {isLoading ? <p className="text-sm text-muted">Loading…</p> : memberships.length === 0 ? (
        <EmptyState icon={<Building2 className="w-6 h-6" />}>Not a member of any tenant.</EmptyState>
      ) : (
        <div className="divide-y divide-[var(--scpa-border-light)]">
          {memberships.map((m: any) => (
            <div key={m.id} className="flex items-center justify-between py-2.5">
              <div>
                <p className="text-sm font-medium text-fg">{m.company.name}</p>
                <p className="text-xs text-muted font-mono">{m.company.slug}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge label={m.role} variant={m.role === 'owner' ? 'blue' : 'gray'} />
                {!m.company.isActive && <Badge label="Inactive" variant="red" />}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

// ── Email Log Tab ────────────────────────────────────────────
// Platform-wide inbound mail log — includes rows no tenant can see
// (unmatched recipients, RCPT-time rejections). Auto-refreshes for
// live MX/forwarding testing.

const EMAIL_LOG_BADGE: Record<string, { label: string; variant: 'green' | 'red' | 'blue' | 'gray' }> = {
  created: { label: 'Ticket created', variant: 'green' },
  replied: { label: 'Reply added', variant: 'green' },
  processing: { label: 'Processing', variant: 'gray' },
  duplicate: { label: 'Duplicate', variant: 'gray' },
  rejected_no_integration: { label: 'No integration', variant: 'red' },
  rejected_unknown_recipient: { label: 'Unknown recipient', variant: 'red' },
  rejected_loop: { label: 'Auto-reply/loop', variant: 'blue' },
  rejected_spam: { label: 'Filtered', variant: 'blue' },
  error: { label: 'Error', variant: 'red' },
};

function EmailLogTab() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const { data, isFetching, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['admin-email-logs', page],
    queryFn: () => adminApi.getInboundEmailLogs({ page, limit: 50 }),
    refetchInterval: 5000,
  });
  const logs = data?.logs ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  const reprocessMut = useMutation({
    mutationFn: (id: string) => adminApi.reprocessInboundEmailLog(id),
    onSuccess: (result) => {
      if (result.ok) toast.success(result.ticketNumber ? `Reprocessed → ${result.ticketNumber}` : 'Reprocessed');
      else toast.error(`Still not processed: ${result.error || result.status}`);
      qc.invalidateQueries({ queryKey: ['admin-email-logs'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message || e.message || 'Reprocess failed'),
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted">
          Every inbound SMTP delivery attempt across all tenants — including mail rejected before it
          matched a tenant. Auto-refreshes every 5s
          {dataUpdatedAt ? ` (last: ${new Date(dataUpdatedAt).toLocaleTimeString()})` : ''}.
        </p>
        <button className="btn btn-secondary text-sm" onClick={() => refetch()} disabled={isFetching}>
          Refresh
        </button>
      </div>

      <div className="card overflow-hidden p-0">
        {logs.length === 0 ? (
          <EmptyState icon={<Shield className="w-6 h-6" />}>
            No inbound mail recorded yet. A connection that never reaches the server (DNS/firewall)
            leaves no trace here — check `docker logs` on the VM for connection-level events.
          </EmptyState>
        ) : (
          <table className="scpa-table">
            <thead>
              <tr>
                <th className="scpa-th">Received</th>
                <th className="scpa-th">From</th>
                <th className="scpa-th">To</th>
                <th className="scpa-th">Subject</th>
                <th className="scpa-th">Outcome</th>
                <th className="scpa-th">Tenant</th>
                <th className="scpa-th">Ticket</th>
                <th className="scpa-th"></th>
              </tr>
            </thead>
            <tbody>
              {logs.map(row => {
                const badge = EMAIL_LOG_BADGE[row.status] || { label: row.status, variant: 'gray' as const };
                return (
                  <tr key={row.id} className="scpa-row" style={{ cursor: 'default' }}>
                    <td className="scpa-td text-muted whitespace-nowrap">{new Date(row.createdAt).toLocaleString()}</td>
                    <td className="scpa-td text-fg">{row.fromEmail || '—'}</td>
                    <td className="scpa-td text-muted">{row.toEmail || '—'}</td>
                    <td className="scpa-td text-muted max-w-[220px] truncate" title={row.subject}>{row.subject || '—'}</td>
                    <td className="scpa-td" title={row.error || undefined}>
                      <Badge label={badge.label} variant={badge.variant} />
                    </td>
                    <td className="scpa-td text-muted">{row.companyName || '—'}</td>
                    <td className="scpa-td font-mono text-xs text-muted">{row.ticketNumber || '—'}</td>
                    <td className="scpa-td text-right">
                      {row.canReprocess && (
                        <button
                          className="btn btn-ghost text-sm"
                          title="Run this email through the pipeline again"
                          disabled={reprocessMut.isPending}
                          onClick={() => reprocessMut.mutate(row.id)}
                        >
                          Reprocess
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-2 border-t border-line text-sm text-muted">
            <button className="btn btn-ghost text-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
            <span>Page {page} of {totalPages}</span>
            <button className="btn btn-ghost text-sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Admin Page ──────────────────────────────────────────

export default function Admin() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'tenants' | 'users' | 'email-log'>('overview');

  const { data: stats } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: () => adminApi.getStats(),
    enabled: !!user?.isSuperAdmin,
  });

  if (!user?.isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="flex flex-col gap-4 pb-10">
      <PageHeader
        title="Global Admin"
        intro="Manage every tenant and user across the platform. Use the tabs to view stats, create tenants, or manage user accounts."
      />

      <div className="flex gap-1 border-b border-line">
        {(['overview', 'tenants', 'users', 'email-log'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors capitalize ${
              activeTab === tab
                ? 'border-primary text-primary'
                : 'border-transparent text-muted hover:text-fg'
            }`}
          >
            {tab === 'tenants' ? 'Tenants' : tab === 'users' ? 'Users' : tab === 'email-log' ? 'Email Log' : 'Overview'}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && <OverviewTab stats={stats} />}
      {activeTab === 'tenants' && <TenantsTab />}
      {activeTab === 'users' && <UsersTab />}
      {activeTab === 'email-log' && <EmailLogTab />}
    </div>
  );
}
